/**
 * CroppedCaptionResizer Class
 * Adjusts the height of an absolutely positioned lazy-img__mod element
 * to account for a sibling figcaption's height and margin-top.
 */
class CroppedCaptionResizer {
  static instances = [];
  static resizeTimeout;

  constructor(containerElement) {
    this.containerElement = containerElement;
    this.imgElement = this.containerElement.querySelector('img');
    this.parentContainer = this.containerElement.parentElement; // This will be .article-asset-mod.has-caption
    this.figcaption = this.parentContainer ? this.parentContainer.querySelector('figcaption') : null;

    // Bind `this` context for event listeners
    this.handleLazyloaded = this.handleLazyloaded.bind(this);
    this.adjustHeight = this.adjustHeight.bind(this);

    // Add this instance to the static list
    CroppedCaptionResizer.instances.push(this);

    // Listen for the 'lazyloaded' event on this specific image
    this.imgElement.addEventListener('lazyloaded', this.handleLazyloaded);

    // Initial adjustment for images that might already be loaded
    // (e.g., if they are in the initial viewport or not lazy initially)
    if (this.imgElement.complete || this.imgElement.classList.contains('lazyloaded')) {
      this.adjustHeight();
    }
  }

  /**
   * Handles the 'lazyloaded' event for the image.
   * Ensures the adjustment happens after the image is fully loaded.
   */
  handleLazyloaded(e) {
    // Only proceed if the event target is *this* image element
    if (e.target === this.imgElement) {
      this.adjustHeight();
    }
  }

  /**
   * Calculates and applies the correct height to the image.
   */
  adjustHeight() {
    // Basic null checks for safety, though parentContainer should always exist if constructor ran.
    if (!this.containerElement || !this.parentContainer || !this.imgElement) {
        return;
    }

    // If no figcaption, the image should take 100% of the parent's height
    if (!this.figcaption) {
      this.imgElement.style.height = '100%';
      return;
    }

    // Get the computed style of the figcaption to read its margin-top
    const figcaptionComputedStyle = window.getComputedStyle(this.figcaption);
    let figcaptionMarginTop = parseFloat(figcaptionComputedStyle.marginTop);

    // Account for padding bottom in grid
    const {paddingBottom} = window.getComputedStyle(this.parentContainer);
    if (paddingBottom && paddingBottom !== '0px') {
      figcaptionMarginTop += parseFloat(paddingBottom);
    }

    // Calculate the total space taken by the figcaption
    // offsetHeight includes content, padding, and border
    const figcaptionTotalSpace = this.figcaption.offsetHeight + figcaptionMarginTop;

    // Get the full height of the parent container
    // offsetHeight includes content, padding, and border of the parent
    const parentFullHeight = this.parentContainer.offsetHeight;

    // Calculate the new height for the image
    const newImageHeight = parentFullHeight - figcaptionTotalSpace;

    // Apply the new height to the image
    this.containerElement.style.height = Math.max(0, newImageHeight) + 'px';
  }

  /**
   * Disposes the instance by removing event listeners and from static list.
   * Call this if elements are dynamically removed from the DOM.
   */
  dispose() {
    this.imgElement.removeEventListener('lazyloaded', this.handleLazyloaded);
    const index = CroppedCaptionResizer.instances.indexOf(this);
    if (index > -1) {
      CroppedCaptionResizer.instances.splice(index, 1);
    }
  }
}

// Global resize event listener (debounced)
// It iterates over all active instances to re-adjust them.
window.addEventListener('resize', () => {
  clearTimeout(CroppedCaptionResizer.resizeTimeout);
  CroppedCaptionResizer.resizeTimeout = setTimeout(() => {
    CroppedCaptionResizer.instances.forEach(instance => instance.adjustHeight());
  }, 150);
});

export default CroppedCaptionResizer;
